import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { trpc } from '@/lib/trpc';
import { usePermission } from '@/components/guards/permission-guard';
import { showErrorToast, showSuccessToast } from '@/hooks/use-mutation-with-toast';

export type RevenueMarket = 'DOMESTIC' | 'INTERNATIONAL';
export const parseRevenueMarket = (value: string | null): RevenueMarket | undefined =>
  value === 'DOMESTIC' || value === 'INTERNATIONAL' ? value : undefined;

export function RevenueMarketSelect({
  value,
  onChange,
  disabled = false,
}: {
  value?: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const { i18n } = useTranslation();
  const tr = i18n.language.startsWith('tr');
  return (
    <label className="flex flex-wrap items-center gap-3 text-sm">
      <span>{tr ? 'Satış pazarı' : 'Sales market'}</span>
      <select
        aria-label={tr ? 'Satış pazarı' : 'Sales market'}
        value={value ?? ''}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="bg-background h-9 border px-3"
      >
        <option value="">
          {tr ? 'Belirtilmemiş (mevcut gelir tanımı)' : 'Unassigned (existing revenue mapping)'}
        </option>
        <option value="DOMESTIC">{tr ? 'Yurtiçi' : 'Domestic'}</option>
        <option value="INTERNATIONAL">{tr ? 'Yurtdışı' : 'International'}</option>
      </select>
    </label>
  );
}

export function RevenueMarketEditor({
  kind,
  id,
  value,
  booking,
}: {
  kind: 'booking' | 'invoice';
  id: string;
  value?: string | null;
  booking?: { id: string; revenueMarket?: string | null } | null;
}) {
  const { i18n } = useTranslation();
  const tr = i18n.language.startsWith('tr');
  const canEdit = usePermission(kind === 'booking' ? 'BOOKINGS' : 'INVOICES', 'EDIT');
  const utils = trpc.useUtils();
  const options = {
    onSuccess: () => {
      void utils.booking.invalidate();
      void utils.invoice.invalidate();
      showSuccessToast(tr ? 'Satış pazarı güncellendi.' : 'Sales market updated.');
    },
    onError: (error: { message: string }) => showErrorToast(error.message),
  };
  const bookingMutation = trpc.booking.setRevenueMarket.useMutation(options);
  const invoiceMutation = trpc.invoice.setRevenueMarket.useMutation(options);
  return (
    <div className="bg-card space-y-2 rounded-lg border p-3">
      <RevenueMarketSelect
        value={booking ? booking.revenueMarket : value}
        disabled={!canEdit || !!booking || bookingMutation.isPending || invoiceMutation.isPending}
        onChange={(raw) =>
          (kind === 'booking' ? bookingMutation : invoiceMutation).mutate({
            id,
            revenueMarket: parseRevenueMarket(raw) ?? null,
          })
        }
      />
      {booking && (
        <Link className="text-sm underline" to={`/travel/bookings/${booking.id}`}>
          {tr
            ? 'Satış pazarını bağlı rezervasyondan değiştir'
            : 'Change the sales market on the linked booking'}
        </Link>
      )}
      <p className="text-muted-foreground text-xs">
        {tr
          ? 'Yurtiçi/yurtdışı gelir hesabını belirler. Mevcut muhasebe fişleri değişmez; seçim sonraki muhasebeleştirmelerde uygulanır.'
          : 'Selects the domestic/international revenue account. Existing journals stay unchanged; the selection applies to subsequent postings.'}
      </p>
    </div>
  );
}
